import {
  Users,
  ChevronDown,
  Maximize,
  PictureInPicture2,
  Volume1,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useRoomController, useRoomState } from "../../room/RoomContext";
import { initials } from "../../room/initials";
import { ViewerConnectionQuality } from "../media/ViewerConnectionQuality";
import { SpectatorsControl } from "../media/SpectatorsControl";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverHeader,
  PopoverTitle,
} from "../ui/popover";
import { Slider } from "../ui/slider";
import {
  focusedControlOpenState,
  focusedControlChrome,
  focusedControlWrap,
} from "./focused-overlay-styles";

function FocusDetails() {
  const { t } = useTranslation();
  const name = useRoomState((s) => s.stage.focusName);
  const picture = useRoomState((s) => s.stage.focusPicture);
  const live = useRoomState((s) => s.stage.focusLive);
  return (
    <div
      id="focusDetails"
      data-focused-overlay-use
      className="flex min-h-9.5 min-w-0 items-center gap-2 rounded-lg bg-zinc-950/82 px-2 py-1 shadow-lg backdrop-blur-sm"
      hidden={!name}
    >
      <Avatar className="size-7">
        <AvatarImage
          src={picture || undefined}
          alt=""
          referrerPolicy="no-referrer"
          className="object-cover"
        />
        <AvatarFallback>{initials(name)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <strong className="block truncate text-xs">{name || t("focused.member")}</strong>
      </div>
      <span
        className="rounded-sm bg-live px-1.5 py-0.5 text-xs font-black text-white"
        hidden={!live}
      >
        {t("common.live")}
      </span>
    </div>
  );
}

function VolumeControl() {
  const { t } = useTranslation();
  const controller = useRoomController();
  const visible = useRoomState((s) => s.stage.focusVolumeVisible);
  const available = useRoomState((s) => s.stage.focusVolumeAvailable);
  const open = useRoomState((s) => s.stage.focusVolumeOpen);
  const volume = useRoomState((s) => s.stage.focusVolume);
  const muted = useRoomState((s) => s.stage.focusMuted);
  const VolumeIcon = muted ? VolumeX : volume < 50 ? Volume1 : Volume2;
  return (
    <div
      id="focusVolumeWrap"
      data-focused-overlay-use
      className={focusedControlWrap}
      hidden={!visible}
    >
      <Popover open={open} onOpenChange={controller.setFocusedVolumeOpen}>
        <PopoverTrigger asChild>
          <Button
            id="focusVolumeAction"
            variant="ghost"
            size="icon-sm"
            className={`text-muted-foreground ${focusedControlOpenState}`}
            disabled={!available}
            aria-label={t(available ? "focused.volumeControls" : "focused.noSharedAudio")}
            title={t(
              !available
                ? "focused.noSharedAudio"
                : muted
                  ? "focused.muted"
                  : "focused.volumePercent",
              { value: volume },
            )}
          >
            <VolumeIcon aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          id="focusVolumePopover"
          side="bottom"
          align="end"
          sideOffset={12}
          alignOffset={-4}
          collisionPadding={8}
          aria-labelledby="focusVolumeTitle"
          className="flex w-20 flex-col-reverse items-center gap-3"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <Button
            id="focusMuteAction"
            variant="ghost"
            size="icon-sm"
            disabled={!available}
            aria-label={t(muted ? "focused.unmuteStream" : "focused.muteStream")}
            title={t(muted ? "focused.unmute" : "focused.mute")}
            onClick={controller.toggleFocusedMute}
          >
            <VolumeIcon aria-hidden="true" />
          </Button>
          <Slider
            id="focusVolume"
            orientation="vertical"
            className="h-28"
            min={0}
            max={100}
            step={1}
            value={[volume]}
            disabled={!available}
            aria-label={t("focused.streamVolume")}
            onValueChange={([value]) => controller.setFocusedVolume(value)}
          />
          <PopoverHeader className="sr-only">
            <PopoverTitle id="focusVolumeTitle">{t("focused.volumeControls")}</PopoverTitle>
          </PopoverHeader>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function ViewControls() {
  const { t } = useTranslation();
  const controller = useRoomController();
  const visible = useRoomState((s) => s.stage.focusViewVisible);
  const pipAvailable = useRoomState((s) => s.stage.focusPipAvailable);
  return (
    <div
      id="focusViewWrap"
      data-focused-overlay-use
      className={focusedControlWrap}
      hidden={!visible}
    >
      <Button
        id="focusPipAction"
        variant="ghost"
        size="icon-sm"
        className="text-muted-foreground"
        disabled={!pipAvailable}
        aria-label={t("focused.pip")}
        onClick={() => void controller.enterPictureInPicture()}
      >
        <PictureInPicture2 aria-hidden="true" />
      </Button>
      <Button
        id="focusFullscreenAction"
        variant="ghost"
        size="icon-sm"
        className="text-muted-foreground"
        aria-label={t("focused.enterFullscreen")}
        title={t("focused.fullscreen")}
        onClick={controller.toggleFocusedFullscreen}
      >
        <Maximize aria-hidden="true" />
      </Button>
    </div>
  );
}

function ParticipantsToggle() {
  const { t } = useTranslation();
  const controller = useRoomController();
  const visible = useRoomState((s) => s.stage.participantsToggleVisible);
  const hidden = useRoomState((s) => s.stage.participantCardsHidden);
  const label = t(hidden ? "focused.showCards" : "focused.hideCards");
  return (
    <div
      id="participantsToggleOverlay"
      data-participants-toggle
      className={`absolute bottom-(--focus-toggle-bottom) left-1/2 z-20 -translate-x-1/2 translate-y-1 ${focusedControlChrome} pointer-events-none invisible opacity-0 transition-[opacity,visibility,transform] [transition-delay:0s,160ms,0s] duration-160 group-data-[focus-layout=camera]/stage:bottom-(--focus-toggle-bottom-camera) group-data-[focus-layout=hidden]/stage:bottom-3 group-data-[overlays-visible]/stage:pointer-events-auto group-data-[overlays-visible]/stage:visible group-data-[overlays-visible]/stage:translate-y-0 group-data-[overlays-visible]/stage:opacity-100 group-data-[overlays-visible]/stage:delay-0`}
      hidden={!visible}
    >
      <Button
        id="toggleCardsAction"
        variant="ghost"
        size="sm"
        className="participants-toggle-action text-muted-foreground"
        type="button"
        aria-label={label}
        aria-expanded={!hidden}
        title={label}
        onClick={controller.toggleParticipantCards}
      >
        <Users aria-hidden="true" />
        <ChevronDown
          aria-hidden="true"
          className={`participants-toggle-chevron transition-transform duration-160 ${hidden ? "rotate-180" : ""}`}
        />
      </Button>
    </div>
  );
}

export function FocusedStreamOverlays() {
  const { t } = useTranslation();
  const controller = useRoomController();
  const visible = useRoomState((s) => s.stage.focusedOverlaysVisible);
  const closeVisible = useRoomState((s) => s.stage.focusCloseVisible);
  const stoppingWatch = useRoomState((s) => s.stage.focusStoppingWatch);
  const name = useRoomState((s) => s.stage.focusName);
  return (
    <>
      <div
        data-focused-stream-overlay
        style={{
          transform: visible ? "translateY(0)" : "translateY(-0.25rem)",
          transition: visible
            ? "opacity .16s ease, visibility 0s, transform .16s ease"
            : "opacity .16s ease, visibility 0s linear .16s, transform .16s ease",
        }}
        className={`pointer-events-none absolute top-3 right-[clamp(0.75rem,8vw,4.75rem)] left-[max(3.5rem,clamp(0.75rem,8vw,4.75rem))] z-20 flex items-start justify-between gap-3 max-[53.75rem]:right-4.5 max-[40.625rem]:top-2 max-[40.625rem]:right-2 ${visible ? "visible opacity-100" : "invisible opacity-0"}`}
      >
        <div className="pointer-events-auto flex min-w-0 items-start gap-2">
          <FocusDetails />
          <SpectatorsControl kind="viewer" className={focusedControlWrap} />
          <ViewerConnectionQuality />
        </div>
        <div className="pointer-events-auto flex items-start gap-2">
          <VolumeControl />
          <ViewControls />
          <Button
            id="focusCloseAction"
            data-focused-overlay-use="close"
            variant="secondary"
            size="icon"
            className="grid size-9.5 place-items-center rounded-lg border-0 p-0 shadow-lg [&_svg]:size-4 [&_svg]:stroke-[2.4]"
            hidden={!closeVisible}
            aria-label={
              stoppingWatch ? t("focused.stopWatchingName", { name }) : t("focused.closeStream")
            }
            title={t(stoppingWatch ? "focused.stopWatching" : "common.close")}
            onClick={controller.closeFocusedStream}
          >
            <X aria-hidden="true" />
          </Button>
        </div>
      </div>
      <ParticipantsToggle />
    </>
  );
}
